import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { checkAuth } from "../libs/auth";
import { setUser, setUserToken } from "../libs/user";
import { getUserProfile } from "../services/auth";
import FullScreenSkeleton from "./Skeletons/FullScreenSkeleton";
import { APPLIED_DESIGN_VARIANT, APPLIED_TOI_LANDING_PAGE } from "../constants";

const Protected = ({ children }) => {
  const [searchParams] = useSearchParams();
  const extToken = searchParams.get("token");
  let channel = searchParams.get("channel") || "";
  const theme = searchParams.get("theme") || "";

  const [TOIChannelData, setTOIChannelData] = useState(false);

  const sessionSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  };

  const safeJsonParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  };

  // Apply URL params safely (avoid doing side-effects during render)
  useEffect(() => {
    if (extToken) {
      setUserToken(extToken);
      getUserProfile(extToken)
        .then((response) => {
          setUser(response);
        })
        .catch(() => {});
    }
  }, [extToken]);

  useEffect(() => {
    if (channel) {
      sessionSet("channel", channel);
    }
    if (theme) {
      sessionSet("theme", theme);
      try {
        document.documentElement.setAttribute("data-theme", theme);
      } catch (e) {
        // ignore
      }
    }
  }, [channel, theme]);

  useEffect(() => {
    if (channel === "TOI") {
      if (
        typeof window.webkit?.messageHandlers?.tilAppWebBridge !== "undefined"
      ) {
        window.webkit.messageHandlers.tilAppWebBridge.postMessage({
          requestReason: "fetchAppData",
          type: "abound",
          value: "",
        });
      }
      if (typeof window.tilAppWebBridge !== "undefined") {
        window.tilAppWebBridge.postMessage(
          JSON.stringify({
            requestReason: "fetchAppData",
            type: "abound",
            value: "",
          }),
        );
      }
    }
  }, [channel]);

  const handleMessageFromApp = useCallback((data) => {
    try {
      let raw = data;
      if (typeof raw === "string") {
        raw = safeJsonParse(raw) || raw;
      }
      const response = raw?.response;
      let storedUserData = typeof response === "string" ? response : response;
      if (typeof storedUserData === "string") {
        storedUserData = storedUserData.replace(/^'|'$/g, "");
        storedUserData = safeJsonParse(storedUserData);
      }
      if (!storedUserData || typeof storedUserData !== "object") return;

      // Persist theme/channel/email if possible
      sessionSet("theme", storedUserData.appTheme);
      sessionSet("channel", storedUserData.channel);
      if (typeof storedUserData.email !== "undefined" && storedUserData.email) {
        sessionSet("email", storedUserData.email);
      }
      try {
        document.documentElement.setAttribute(
          "data-theme",
          storedUserData.appTheme,
        );
      } catch (e) {
        // ignore
      }

      if (storedUserData.token) {
        setUserToken(storedUserData.token);
      }

      setTOIChannelData(true);
    } catch (e) {
      // ignore
    }
  }, []);

  // Install handler and immediately consume any early payload stored by tc-web-bridge.js
  useEffect(() => {
    window.messageFromApp = handleMessageFromApp;
    try {
      const queued = window.sessionStorage.getItem("callbackData");
      if (queued) {
        handleMessageFromApp(queued);
        window.sessionStorage.removeItem("callbackData");
      }
    } catch (e) {
      // ignore
    }
  }, [channel, handleMessageFromApp]);

  if (channel === "TOI" && !TOIChannelData) {
    return <FullScreenSkeleton />;
  }
  const isLoggedIn = checkAuth();
  if (!isLoggedIn && channel === "TOI") {
    // const appliedLandingPage = APPLIED_TOI_LANDING_PAGE[APPLIED_DESIGN_VARIANT];
    return <Navigate to={"/onboarding/landing"} replace />;
  }
  if (!isLoggedIn) {
    return <Navigate to="/login?type=mobile" replace />;
  }
  return children;
};

export default Protected;
